import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { SeverityBadge, StatusBadge } from "@/components/Primitives";
import { useStatuses } from "@/context/StatusContext";
import { useAuth } from "@/context/AuthContext";
import { fmtTime } from "@/lib/time";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, MessageSquare, Paperclip, Plus, RotateCcw, Search, Star, Ticket, X } from "lucide-react";

// A short, friendly picklist rather than the free-text field staff get
// (`NewTicketDialog.jsx`'s `category` input): `category` has no fixed
// vocabulary anywhere in this backend -- it is whatever string a queue or a
// custom-field definition happens to key on -- so asking a customer to type
// one invites a typo that never routes anywhere. "general" stays the
// server's own default value if nothing is picked.
const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "hardware", label: "Hardware" },
  { value: "software", label: "Software" },
  { value: "network", label: "Network" },
  { value: "access", label: "Account Access" },
  { value: "security", label: "Security" },
  { value: "billing", label: "Billing Question" },
  { value: "other", label: "Other" },
];

const MAX_ATTACHMENT_MB = 25;

/**
 * The client portal ticket list and ticket detail -- Task 10 of sub-project I.
 *
 * Both routes render from this one file (`/portal/tickets` and
 * `/portal/tickets/:id`), the way `ClientPortal.jsx` next door renders its
 * own surveys and overview together: `api.get`, `useEffect`, the same
 * Card/Badge vocabulary, the zinc dark theme.
 *
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DO:
 *
 *  - It does not filter anything the server already filtered. The
 *    conversation below is rendered straight from the `comments` array
 *    `GET /tickets/{id}` returned; that route already drops internal notes
 *    for a client-scoped caller (`server.py`, `comment_q["internal"] =
 *    {"$ne": True}`). Re-filtering here would be a second implementation of
 *    the one rule that matters, and a browser-side filter is not a guarantee
 *    in any case -- a customer holds their own session cookie and can read
 *    the JSON directly. `backend/tests/test_portal_tickets.py` is where that
 *    guarantee actually lives.
 *
 *  - It renders NO custom fields. THIS IS A DECISION, NOT AN OVERSIGHT.
 *    `ticket_fields` ships no staff-only flag, so every definition an
 *    administrator creates for a category applies to every reader of that
 *    category's tickets -- there is no way for this screen to tell "asset
 *    tag" from "escalation risk". Until such a flag exists the portal shows
 *    none of them, which is the narrow direction. The server agrees:
 *    `/api/ticket-field-defs/*` is absent from `authz.PORTAL_ALLOW`, so a
 *    portal account cannot fetch the definitions to label a value with even
 *    if a future edit here tried to.
 *
 *  - It does not show a merged duplicate in the list. `list_tickets` sets
 *    `merged_into = psa.NOT_MERGED` unconditionally, so the husk is absent
 *    from the portal exactly as it is from every staff queue -- and a merge
 *    MOVES the comments to the survivor, so the customer's conversation
 *    continues on a ticket they already see. A bookmarked link to the husk
 *    still opens, and gets the banner below pointing at the survivor.
 *
 *  - It does not reuse `NewTicketDialog`. That component calls `/clients`,
 *    `/users`, `/desk/templates`, `/desk/sla/preview` and
 *    `/ticket-field-defs/for-category`, every one of which a portal account
 *    is refused. The small form below sends exactly the three fields
 *    `CREATE_TICKET_ALLOWED_FIELDS` permits it to; `client_id` and the
 *    requester are set by the server from the caller's own account, so
 *    sending them would be theatre.
 */

const PAGE = 25;

// Mirrors `backend/authz.py`'s CLIENT_SCOPED_ROLES, the same way
// `NewTicketDialog.jsx` already does. Used for COPY ONLY -- nothing on this
// screen is gated on it, because a client-side role list is not a guard and
// every guarantee here is enforced server-side. See `staffViewer` below.
const CLIENT_SCOPED_ROLES = [
  "client_read_only", "client_admin", "client_administrator",
  "client_manager", "client_user", "client_contact",
];

// Mirrors `backend/authz.py`'s READ_ONLY_CLIENT_ROLES. The product owner
// decided during the Task 10 review that `client_read_only` means what it
// says: the server refuses it every non-safe method inside
// `client_scope_guard`, with a 403 that names the reason.
//
// ⚠️ THIS LIST HIDES CONTROLS AND GUARDS NOTHING. The refusal is the
// server's; this exists only so nobody is invited to press a button that
// would 403 at them. If the two ever disagree the server wins, which is the
// correct direction: the failure is a control that is missing rather than a
// control that is fake.
const READ_ONLY_CLIENT_ROLES = ["client_read_only"];

function useReadOnlyPortal() {
  const { user } = useAuth();
  return !!user && READ_ONLY_CLIENT_ROLES.includes(user.role);
}

// A durable, page-level banner for anything that failed. A toast is gone in
// four seconds and this app's single <Toaster> has no richColors, so an
// error toast is the same neutral colour as a success one -- matching Task
// 9's screens, which carry the same pair for the same reason.
function ErrorBanner({ children, testid }) {
  if (!children) return null;
  return (
    <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded-md px-4 py-3 text-sm"
         data-testid={testid}>
      {children}
    </div>
  );
}

export default function PortalTickets() {
  const { id } = useParams();
  return id ? <PortalTicketDetail id={id} /> : <PortalTicketList />;
}

// --------------------------------------------------------------------------- //
// The list
// --------------------------------------------------------------------------- //
function PortalTicketList() {
  const [params, setParams] = useSearchParams();
  const { statuses } = useStatuses();
  const { user } = useAuth();
  const [body, setBody] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [raising, setRaising] = useState(false);
  // Request sequence number. See `load` below.
  const seq = useRef(0);

  // COPY ONLY. MSP staff can reach this screen by typing the URL, and
  // `client_query` correctly does not narrow for them -- so they see every
  // ticket in the tenant. The rows are ones they are entitled to; what was
  // WRONG was the sentence above them, which said "Your tickets" and
  // "Everything your organisation has raised with us" over 4,576 rows
  // belonging to every client the MSP has.
  //
  // ⚠️ This is the FIRST /portal/* screen that renders unscoped tenant data
  // to staff. The Task 10 report originally called it "pre-existing shape,
  // the same is true of /portal today" and that was simply false:
  // `portal_overview` raises 403 for any caller with no `client_id`, so
  // `ClientPortal.jsx` shows staff "Loading portal…" forever and no data at
  // all. Corrected here as well as in the report, because a false reason is
  // what stops the next person looking.
  const staffViewer = !!user && !CLIENT_SCOPED_ROLES.includes(user.role);
  const readOnly = useReadOnlyPortal();

  const status = params.get("status") || "";
  const q = params.get("q") || "";
  const offset = Number(params.get("offset") || 0);

  // One setParams per change, built from a fresh copy: two calls made from
  // the same render-time `params` each start from the pre-update snapshot,
  // so the second silently overwrites the first.
  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k !== "offset") next.delete("offset");   // a new filter starts at page 1
    setParams(next, { replace: true });
  };

  // Paged through offset/limit against the `total` the endpoint returns --
  // never by fetching everything and slicing here. `list_tickets`' own
  // docstring records that defect: the page used to fetch limit=500 and
  // filter in the browser, so every filter was wrong above 500 rows and the
  // count in the heading was the count of whatever happened to be fetched.
  // ⚠️ EVERY RESPONSE CARRIES A SEQUENCE NUMBER AND A STALE ONE IS DROPPED.
  // `load` is a useCallback over [params], so the explicit `load()` after
  // raising a ticket fires against the PRE-update params while `setParams`
  // re-runs the effect with the new ones: two GETs in flight, and whichever
  // resolves last repaints the table. Raise a ticket from page 3 and the
  // stale response could repaint page 3 while the URL says page 1 and the
  // new ticket is nowhere on screen.
  //
  // The explicit call is NOT the bug and is not removed: `setParams` with a
  // string equal to the current one does not re-fire the effect, so deleting
  // it would mean a ticket raised from page 1 never appearing until a manual
  // reload. Two requests is the correct behaviour; two requests where the
  // loser can win is not.
  const load = useCallback(() => {
    const mine = ++seq.current;
    setLoading(true);
    const qs = new URLSearchParams();
    if (params.get("status")) qs.set("status", params.get("status"));
    if (params.get("q")) qs.set("q", params.get("q"));
    qs.set("limit", String(PAGE));
    qs.set("offset", params.get("offset") || "0");
    api.get(`/tickets?${qs}`)
      .then((r) => {
        if (mine !== seq.current) return;      // a newer request has been made
        setErr("");
        setBody(r.data && typeof r.data === "object" && r.data.items
          ? r.data
          : { items: asArray(r.data), total: asArray(r.data).length });
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        setErr(formatApiError(e));
        setBody({ items: [], total: 0 });
      })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil((body.total || 0) / PAGE));
  const page = Math.floor(offset / PAGE) + 1;

  // ⚠️ AN OUT-OF-RANGE PAGE TOLD A CUSTOMER THEY HAD NO TICKETS AND OFFERED
  // NO WAY BACK. A customer with 26 tickets sits on page 2; the desk merges
  // two duplicates, `total` drops to 24, and the next load returns an empty
  // page. The empty state then read "No tickets yet. Raise one and it will
  // appear here." -- a false statement to somebody holding 24 tickets --
  // under a heading saying "(24)", and the pager was hidden because
  // `pages > 1` was false, so the only way out was editing the URL.
  //
  // Corrected by walking back to the first page, which is what the customer
  // wanted; the pager below ALSO renders whenever `offset > 0`, so even
  // during the correction, or if `total` is momentarily unknown, there is a
  // visible way back rather than a dead end. Cannot loop: the correction
  // only fires while `offset > 0`, and it sets `offset` to nothing.
  const outOfRange = offset > 0 && offset >= (body.total || 0);
  useEffect(() => {
    if (!loading && outOfRange) set("offset", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, outOfRange]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
            {staffViewer ? "Client Portal · staff view" : "Client Portal"}
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Ticket size={22} className="text-blue-400" />
            {staffViewer ? "Portal ticket list" : "Your tickets"}
            <span className="text-zinc-500">({body.total || 0})</span>
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {staffViewer
              ? "This is the screen a customer sees. You are staff, so it is not scoped to one client, and this count is every ticket in the tenant."
              : "Everything your organisation has raised with us, newest first."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/portal" data-testid="portal-back-to-overview"
                className="border border-zinc-800 text-zinc-300 rounded-md px-3 py-1.5 text-sm hover:border-zinc-700">
            Portal overview
          </Link>
          {/* Absent, not disabled: a greyed-out button is still an invitation
              and still asks the customer to work out why. The server refuses
              this account the write regardless (`authz._refuse_if_read_only_
              write`), so what is hidden here is a control that could only
              ever have produced a 403. */}
          {!readOnly && (
            <button onClick={() => setRaising(true)} data-testid="portal-raise-btn"
                    className="bg-blue-600 hover:bg-blue-500 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5">
              <Plus size={15} /> Raise a ticket
            </button>
          )}
        </div>
      </div>

      <ErrorBanner testid="portal-tickets-error">{err}</ErrorBanner>

      {raising && !readOnly && (
        <RaiseTicketForm onClose={() => setRaising(false)}
                         onRaised={() => { setRaising(false); set("offset", ""); load(); }} />
      )}

      {readOnly && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-3 text-sm text-zinc-400"
             data-testid="portal-read-only-notice">
          {/* In WORDS, once, at the top, rather than silently omitting the
              controls and leaving the customer to wonder. The same sentence
              the server's own 403 gives. */}
          This account can view your organisation's tickets but cannot raise one
          or add a comment. Ask your administrator for an account that can.
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input defaultValue={q}
                 onKeyDown={(e) => e.key === "Enter" && set("q", e.target.value)}
                 onBlur={(e) => set("q", e.target.value)}
                 placeholder="Search by title or number, then Enter"
                 data-testid="portal-ticket-search"
                 className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm w-full focus:outline-none focus:border-blue-500/60" />
        </div>
        {/* Options come from GET /desk/statuses, which is portal-allowed on
            purpose: the same eight rows for everyone, no tenant data in
            them, and refusing it would break this filter rather than
            protect anything. */}
        <select value={status} onChange={(e) => set("status", e.target.value)}
                data-testid="portal-status-filter"
                className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm min-w-0 max-w-[12rem] shrink">
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr>
              <th className="text-left px-4 py-3 w-20">#</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Priority</th>
              <th className="text-left px-4 py-3">SLA</th>
              <th className="text-left px-4 py-3">Raised by</th>
              <th className="text-left px-4 py-3">Last update</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-zinc-500">Loading…</td></tr>}
            {/* In WORDS, never a dash: an empty grid with a dash in it reads
                as a broken page rather than an empty one. The two cases say
                different things because they mean different things. */}
            {!loading && !body.items.length && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-zinc-500" data-testid="portal-tickets-empty">
                  {/* THREE cases, because they mean three different things.
                      "No tickets yet" is reserved for the one case where it
                      is TRUE: no filter, first page, nothing there. */}
                  {outOfRange
                    ? "There is nothing on this page any more, taking you back to the first page."
                    : (status || q)
                      ? "No tickets match this search. Clear the filters to see them all."
                      : "No tickets yet. Raise one and it will appear here."}
                </td>
              </tr>
            )}
            {!loading && body.items.map((t) => (
              <tr key={t.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-4 py-2 font-mono text-zinc-500 text-xs">#{t.number}</td>
                <td className="px-4 py-2">
                  <Link to={`/portal/tickets/${t.id}`} data-testid={`portal-ticket-link-${t.id}`}
                        className="hover:text-blue-400 inline-flex items-center gap-1.5">
                    {t.vip && (
                      <Star size={12} className="text-amber-400 shrink-0" fill="currentColor"
                            title="VIP contact" data-testid={`portal-ticket-vip-${t.id}`} />
                    )}
                    {t.title}
                  </Link>
                </td>
                <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                <td className="px-4 py-2"><SeverityBadge level={t.priority} /></td>
                {/* `overdue` is computed server-side (psa.annotate_overdue),
                    live, on every read -- never the stored `sla_breached`
                    flag, which is written once at close and is never true
                    of a ticket still open. Blank, not a dash, when it is
                    not overdue: this column exists to flag the exception,
                    not to reassure on every row. */}
                <td className="px-4 py-2" data-testid={`portal-ticket-sla-${t.id}`}>
                  {t.overdue ? (
                    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium bg-red-500/15 text-red-300 border border-red-500/30">
                      <AlertTriangle size={11} /> Past due
                    </span>
                  ) : null}
                </td>
                {/* In WORDS when there is no requester, and words that are
                    TRUE whoever is reading. "Your organisation" was the first
                    wording and is a claim, not a fact: a ticket raised by
                    mail intake that matched no contact carries no requester
                    at all, and staff reaching this screen by URL (see
                    `staffViewer` above) see other clients' rows, for whom
                    that sentence is simply wrong. `requester_name` is
                    attached server-side by
                    `requesters.attach_requester_names`. */}
                <td className="px-4 py-2 text-zinc-400">
                  {typeof t.requester_name === "string" && t.requester_name
                    ? t.requester_name : "Not recorded"}
                </td>
                <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">
                  {fmtTime(t.updated_at || t.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* `|| offset > 0` is the escape hatch: a page that has gone out of
          range must never be a dead end, even for the moment before the
          correction above lands. */}
      {(pages > 1 || offset > 0) && (
        <div className="flex items-center gap-3 text-sm">
          <button disabled={offset <= 0} data-testid="portal-page-prev"
                  onClick={() => set("offset", String(Math.max(0, offset - PAGE)))}
                  className="border border-zinc-800 rounded px-3 py-1 disabled:opacity-40">Previous</button>
          <span className="text-zinc-500 font-mono text-xs">
            Page {page} of {pages} · {body.total} tickets
          </span>
          <button disabled={page >= pages} data-testid="portal-page-next"
                  onClick={() => set("offset", String(offset + PAGE))}
                  className="border border-zinc-800 rounded px-3 py-1 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Raise a ticket
//
// `POST /api/tickets` refuses every key outside
// `CREATE_TICKET_CLIENT_ALLOWED_FIELDS` with a 422, and the server sets
// `client_id` and the requester from the caller's own account whatever the
// body claims -- so a client or requester picker here would be a control
// that looks live and is silently overridden. `department_id` is checked
// there too: it must belong to THIS client, or create refuses with a 422.
//
// Attachments are a SEPARATE request, by necessity: `POST /tickets/{id}/
// attachments` needs a ticket id that does not exist until the ticket itself
// is created. Files are staged locally and uploaded right after, one at a
// time so a single oversized/refused file names itself rather than failing
// the whole batch silently; the ticket already exists by that point, so a
// failed attachment is a warning, never a reason to lose the ticket.
// --------------------------------------------------------------------------- //
function RaiseTicketForm({ onClose, onRaised }) {
  const { user } = useAuth();
  const [f, setF] = useState({
    title: "", description: "", priority: "medium", category: "general",
    department_id: "", contact_name: user?.name || "", contact_phone: "",
    contact_email: user?.email || "",
  });
  const [departments, setDepartments] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyStep, setBusyStep] = useState("");
  const [err, setErr] = useState("");
  const fileInput = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(asArray(r.data))).catch(() => setDepartments([]));
  }, []);

  const addFiles = (list) => {
    const picked = Array.from(list || []);
    const tooBig = picked.filter((file) => file.size > MAX_ATTACHMENT_MB * 1024 * 1024);
    if (tooBig.length) {
      toast.error(`${tooBig.map((file) => file.name).join(", ")} exceeds the ${MAX_ATTACHMENT_MB} MB limit and was not added.`);
    }
    const ok = picked.filter((file) => file.size <= MAX_ATTACHMENT_MB * 1024 * 1024);
    if (ok.length) setFiles((prev) => [...prev, ...ok]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!f.title.trim()) { setErr("A title is required."); return; }
    setBusy(true);
    setBusyStep("Raising ticket…");
    setErr("");
    let ticket;
    try {
      const body = {
        title: f.title.trim(),
        description: f.description.trim(),
        priority: f.priority,
        category: f.category,
      };
      if (f.department_id) body.department_id = f.department_id;
      if (f.contact_name.trim()) body.contact_name = f.contact_name.trim();
      if (f.contact_phone.trim()) body.contact_phone = f.contact_phone.trim();
      if (f.contact_email.trim()) body.contact_email = f.contact_email.trim();
      const { data } = await api.post("/tickets", body);
      ticket = data;
    } catch (e) {
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
      setBusy(false);
      setBusyStep("");
      return;
    }

    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      setBusyStep(`Attaching ${i + 1} of ${files.length}…`);
      const form = new FormData();
      form.append("file", files[i]);
      try {
        await api.post(`/tickets/${ticket.id}/attachments`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        failed += 1;
        toast.error(`${files[i].name}: ${formatApiError(e)}`);
      }
    }

    toast.success(
      failed
        ? `Ticket #${ticket.number} raised — ${files.length - failed} of ${files.length} attachment(s) uploaded`
        : `Ticket #${ticket.number} raised`
    );
    setBusy(false);
    setBusyStep("");
    onRaised?.(ticket);
  };

  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 space-y-3"
         data-testid="portal-raise-form">
      <div className="font-display font-medium">Raise a ticket</div>
      <ErrorBanner testid="portal-raise-error">{err}</ErrorBanner>
      <label className="block">
        <span className="text-xs text-zinc-400">What is the problem?</span>
        <input value={f.title} onChange={(e) => set("title", e.target.value)}
               data-testid="portal-raise-title" autoFocus
               className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
      </label>
      <label className="block">
        <span className="text-xs text-zinc-400">Anything that would help us (optional)</span>
        <textarea value={f.description} onChange={(e) => set("description", e.target.value)}
                  rows={4} data-testid="portal-raise-description"
                  className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block min-w-0">
          <span className="text-xs text-zinc-400">How urgent is it?</span>
          <select value={f.priority} onChange={(e) => set("priority", e.target.value)}
                  data-testid="portal-raise-priority"
                  className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
            {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block min-w-0">
          <span className="text-xs text-zinc-400">Category</span>
          <select value={f.category} onChange={(e) => set("category", e.target.value)}
                  data-testid="portal-raise-category"
                  className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>
        <label className="block min-w-0">
          <span className="text-xs text-zinc-400">Department (optional)</span>
          <select value={f.department_id} onChange={(e) => set("department_id", e.target.value)}
                  data-testid="portal-raise-department"
                  className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
            <option value="">— not specified —</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
        <label className="block min-w-0">
          <span className="text-xs text-zinc-400">Your name</span>
          <input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)}
                 data-testid="portal-raise-contact-name"
                 placeholder="Who should we ask for?"
                 className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
        </label>
        <label className="block min-w-0">
          <span className="text-xs text-zinc-400">Contact email</span>
          <input value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)}
                 type="email" data-testid="portal-raise-contact-email"
                 placeholder="An email we can reach you on"
                 className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
        </label>
        <label className="block min-w-0">
          <span className="text-xs text-zinc-400">Contact number</span>
          <input value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)}
                 data-testid="portal-raise-contact-phone"
                 placeholder="A number we can reach you on"
                 className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
        </label>
      </div>

      <div>
        <span className="text-xs text-zinc-400">Attachments (optional)</span>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => fileInput.current?.click()}
                  data-testid="portal-raise-attach-btn"
                  className="inline-flex items-center gap-1.5 border border-zinc-800 text-zinc-300 rounded-md px-3 py-1.5 text-sm hover:border-zinc-700">
            <Paperclip size={14} /> Add file
          </button>
          <span className="text-[11px] text-zinc-600">Up to {MAX_ATTACHMENT_MB} MB each</span>
          <input ref={fileInput} type="file" multiple className="hidden"
                 data-testid="portal-raise-attach-input"
                 onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </div>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid="portal-raise-attach-list">
            {files.map((file, i) => (
              <li key={`${file.name}-${i}`}
                  className="flex items-center justify-between gap-2 bg-zinc-900/60 border border-zinc-800 rounded px-2.5 py-1.5 text-xs text-zinc-300">
                <span className="truncate">{file.name}</span>
                <span className="text-zinc-500 shrink-0">{(file.size / 1024).toFixed(0)} KB</span>
                <button type="button" onClick={() => removeFile(i)} data-testid={`portal-raise-attach-remove-${i}`}
                        className="text-zinc-500 hover:text-red-400 shrink-0">
                  <X size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex gap-2 items-center">
        <button onClick={submit} disabled={busy} data-testid="portal-raise-submit"
                className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-md px-4 py-1.5 text-sm">
          {busy ? (busyStep || "Raising…") : "Raise ticket"}
        </button>
        <button onClick={onClose} disabled={busy} data-testid="portal-raise-cancel"
                className="border border-zinc-800 text-zinc-300 rounded-md px-3 py-1.5 text-sm disabled:opacity-50">Cancel</button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// The detail
// --------------------------------------------------------------------------- //
function PortalTicketDetail({ id }) {
  const readOnly = useReadOnlyPortal();
  const { byKey: statusByKey } = useStatuses();
  const [t, setT] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [survivor, setSurvivor] = useState(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState("");
  const [departments, setDepartments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [reopening, setReopening] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(() => {
    api.get(`/tickets/${id}`)
      .then((r) => { setLoadErr(""); setT(r.data); })
      .catch((e) => { setLoadErr(formatApiError(e)); setT(null); });
  }, [id]);

  const loadAttachments = useCallback(() => {
    api.get(`/tickets/${id}/attachments`)
      .then((r) => setAttachments(asArray(r.data?.items)))
      .catch(() => setAttachments([]));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAttachments(); }, [loadAttachments]);
  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(asArray(r.data))).catch(() => setDepartments([]));
  }, []);

  const departmentName = (deptId) => departments.find((d) => d.id === deptId)?.name;

  const uploadMore = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    setUploading(true);
    let failed = 0;
    for (const file of picked) {
      if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        toast.error(`${file.name} exceeds the ${MAX_ATTACHMENT_MB} MB limit.`);
        failed += 1;
        continue;
      }
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post(`/tickets/${id}/attachments`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        failed += 1;
        toast.error(`${file.name}: ${formatApiError(e)}`);
      }
    }
    if (picked.length - failed > 0) toast.success("Attachment uploaded");
    setUploading(false);
    loadAttachments();
  };

  // The survivor of a merge, for the banner's number and title. A merged
  // ticket still returns from the API -- deliberately, so this page can
  // exist for a bookmarked link -- and the survivor belongs to the SAME
  // client (a cross-client merge is refused with a 409), so this read is
  // inside the caller's own scope. A failure leaves the banner naming the
  // id alone rather than removing it.
  useEffect(() => {
    if (!t?.merged_into) { setSurvivor(null); return; }
    api.get(`/tickets/${t.merged_into}`).then((r) => setSurvivor(r.data)).catch(() => setSurvivor(null));
  }, [t?.merged_into]);

  // `closes_ticket` comes from the SAME status vocabulary the badge and the
  // filter dropdown already use (`GET /desk/statuses`) -- never a hard-coded
  // ["resolved", "closed"] here that could silently drift from the real
  // vocabulary if an admin ever adds another closing status.
  const canReopen = !!statusByKey[t?.status]?.closes_ticket;

  const reopen = async () => {
    setReopening(true);
    try {
      await api.post(`/tickets/${id}/reopen`);
      toast.success("Ticket reopened");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setReopening(false); }
  };

  const send = async () => {
    if (!draft.trim()) { setPostErr("Write something first."); return; }
    setPosting(true);
    setPostErr("");
    try {
      await api.post(`/tickets/${id}/comments`, { body: draft.trim() });
      setDraft("");
      toast.success("Sent");
      load();
    } catch (e) {
      setPostErr(formatApiError(e));
      toast.error(formatApiError(e));
    } finally { setPosting(false); }
  };

  if (loadErr) {
    return (
      <div className="space-y-4">
        <Link to="/portal/tickets" className="text-sm text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1.5">
          <ArrowLeft size={14} /> Back to your tickets
        </Link>
        <ErrorBanner testid="portal-ticket-load-error">{loadErr}</ErrorBanner>
      </div>
    );
  }
  if (!t) return <div className="text-zinc-500 text-sm">Loading ticket…</div>;

  const merged = !!t.merged_into;
  const comments = asArray(t.comments);

  return (
    <div className="space-y-4">
      <Link to="/portal/tickets" data-testid="portal-ticket-back"
            className="text-sm text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> Back to your tickets
      </Link>

      {merged && (
        <div className="border border-amber-900/50 bg-amber-950/30 text-amber-200 rounded-md px-4 py-3 text-sm"
             data-testid="portal-ticket-merged-banner">
          We combined this ticket with{" "}
          <Link to={`/portal/tickets/${t.merged_into}`} data-testid="portal-ticket-merged-link"
                className="font-mono underline hover:text-amber-100">
            {survivor?.number ? `#${survivor.number}` : "another ticket"}
          </Link>
          {survivor?.title ? ` (${survivor.title})` : ""}, because they were about the same
          thing. Your conversation continues there.
        </div>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
            Ticket #{t.number}
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">{t.title}</h1>
        </div>
        {/* Absent, not disabled, for a read-only account or a merged husk --
            same reasoning as the "Raise a ticket" button on the list: a
            greyed-out control is still an invitation, and the server
            refuses either case regardless (client_read_only via
            `_refuse_if_read_only_write`; a merged ticket via
            `psa.refuse_if_merged`). */}
        {canReopen && !readOnly && !merged && (
          <button onClick={reopen} disabled={reopening} data-testid="portal-ticket-reopen"
                  className="shrink-0 inline-flex items-center gap-1.5 border border-blue-500/40 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 rounded-md px-3 py-1.5 text-sm disabled:opacity-50">
            <RotateCcw size={14} /> {reopening ? "Reopening…" : "Reopen ticket"}
          </button>
        )}
      </div>
      <div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <StatusBadge status={t.status} />
          <SeverityBadge level={t.priority} />
          {t.vip && (
            <span data-testid="portal-ticket-vip-badge"
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30">
              <Star size={11} fill="currentColor" /> VIP
            </span>
          )}
          {t.overdue && (
            <span data-testid="portal-ticket-overdue-badge"
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium bg-red-500/15 text-red-300 border border-red-500/30">
              <AlertTriangle size={11} /> Past due SLA
            </span>
          )}
          <span className="text-xs text-zinc-500 font-mono">
            Raised {fmtTime(t.created_at)} · Last update {fmtTime(t.updated_at || t.created_at)}
          </span>
        </div>
      </div>

      {t.description && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">What you told us</div>
          <div className="text-sm text-zinc-300 whitespace-pre-wrap">{t.description}</div>
        </div>
      )}

      {(t.category || t.department_id || t.contact_name || t.contact_phone || t.contact_email) && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5" data-testid="portal-ticket-details">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">Details</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            {t.category && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Category</div>
                <div className="text-zinc-300 mt-0.5">{CATEGORIES.find((c) => c.value === t.category)?.label || t.category}</div>
              </div>
            )}
            {t.department_id && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Department</div>
                <div className="text-zinc-300 mt-0.5">{departmentName(t.department_id) || "—"}</div>
              </div>
            )}
            {t.contact_name && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Contact</div>
                <div className="text-zinc-300 mt-0.5">{t.contact_name}</div>
              </div>
            )}
            {t.contact_phone && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Contact number</div>
                <div className="text-zinc-300 mt-0.5 font-mono">{t.contact_phone}</div>
              </div>
            )}
            {t.contact_email && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Contact email</div>
                <div className="text-zinc-300 mt-0.5 font-mono truncate">{t.contact_email}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5" data-testid="portal-ticket-attachments">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1.5">
            <Paperclip size={12} /> Attachments
          </div>
          {!readOnly && !merged && (
            <>
              <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading}
                      data-testid="portal-ticket-attach-btn"
                      className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50">
                {uploading ? "Uploading…" : "+ Add file"}
              </button>
              <input ref={fileInput} type="file" multiple className="hidden"
                     data-testid="portal-ticket-attach-input"
                     onChange={(e) => { uploadMore(e.target.files); e.target.value = ""; }} />
            </>
          )}
        </div>
        {attachments.length === 0 ? (
          <div className="text-sm text-zinc-500" data-testid="portal-ticket-no-attachments">
            No files attached.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {attachments.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 text-sm"
                  data-testid={`portal-attachment-${a.id}`}>
                <a href={`${api.defaults.baseURL}/attachments/${a.id}/download`}
                   target="_blank" rel="noreferrer"
                   className="text-zinc-300 hover:text-blue-400 truncate flex items-center gap-1.5">
                  <Paperclip size={12} className="shrink-0 text-zinc-600" /> {a.filename}
                </a>
                <span className="text-[11px] text-zinc-600 shrink-0 font-mono">
                  {((a.size_bytes || 0) / 1024).toFixed(0)} KB · {fmtTime(a.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Approval STATE only. `approval_view` hands a client-scoped caller
          `category_requires_approval`, `state` and `decided_at` and nothing
          else: `decided_by` and `note` come back null by design, because WHO
          decided and WHAT THEY WROTE are the two things the internal-note
          mechanism exists to keep internal. There is nothing to render for
          them here and nothing to defend against -- the server never sends
          them. `can_approve`/`can_reject` are always false for a customer,
          so no control is offered. */}
      {t.approval_view?.category_requires_approval && (
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5"
             data-testid="portal-ticket-approval">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Approval</div>
          <div className="text-sm text-zinc-300">
            {t.approval_view.state === "approved" && (
              <>This work was approved{t.approval_view.decided_at
                ? <> on <span className="font-mono">{fmtTime(t.approval_view.decided_at)}</span></> : null}.</>
            )}
            {t.approval_view.state === "rejected" && (
              <>This work was not approved{t.approval_view.decided_at
                ? <> on <span className="font-mono">{fmtTime(t.approval_view.decided_at)}</span></> : null}.
                Your account manager will be in touch.</>
            )}
            {/* ⚠️ GATED ON STATUS, not on `category_requires_approval`
                alone. A category can acquire an approval rule AFTER a
                ticket in it was resolved -- the rule is looked up live, by
                category, every time (`approvals.rule_for`) and is
                deliberately never cached onto the ticket. So a RESOLVED
                ticket comes back `{category_requires_approval: true,
                state: null}`, and the sentence below told the customer
                "This work needs an approval before it can start." beside a
                Resolved badge. The wording only makes sense while the
                ticket is still open; anywhere else, say what is actually
                known, which is that no decision was recorded. */}
            {!t.approval_view.state && (t.status === "open"
              ? <>This work needs an approval before it can start.</>
              : <>No decision is recorded on this ticket.</>)}
          </div>
        </div>
      )}

      {/* The conversation, rendered from the `comments` the server returned
          and NOT filtered again here. `get_ticket` already drops internal
          notes for a client-scoped caller; a second filter in the browser
          would be a second implementation of the rule that matters, and one
          that a customer can bypass by reading the JSON themselves. */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 space-y-4"
           data-testid="portal-ticket-conversation">
        <div className="font-display font-medium flex items-center gap-2">
          <MessageSquare size={16} className="text-blue-400" /> Conversation
        </div>
        {comments.length === 0 ? (
          <div className="text-sm text-zinc-500" data-testid="portal-ticket-no-comments">
            Nothing has been said on this ticket yet.
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((c) => (
              <div key={c.id} className="border border-zinc-900 rounded-md p-3"
                   data-testid={`portal-comment-${c.id}`}>
                <div className="text-xs text-zinc-500 mb-1">
                  <span className="text-zinc-300">{c.actor_name || "Support"}</span>
                  {" · "}<span className="font-mono">{fmtTime(c.at)}</span>
                </div>
                <div className="text-sm text-zinc-300 whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
          </div>
        )}

        {/* THREE cases, and none of them is a blank space. A conversation
            area that simply ends is the reader's problem to interpret; each
            branch says which of the three reasons applies to them. */}
        {readOnly ? (
          <div className="text-sm text-zinc-500" data-testid="portal-comment-read-only">
            This account can read this conversation but cannot add to it. Ask
            your administrator for an account that can.
          </div>
        ) : merged ? (
          <div className="text-sm text-zinc-500" data-testid="portal-comment-closed">
            This ticket was combined with another one, so replies go there now.
          </div>
        ) : (
          <div className="space-y-2">
            <ErrorBanner testid="portal-comment-error">{postErr}</ErrorBanner>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                      rows={3} placeholder="Add to this ticket"
                      data-testid="portal-comment-box"
                      className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-blue-500/60" />
            <button onClick={send} disabled={posting} data-testid="portal-comment-submit"
                    className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-md px-4 py-1.5 text-sm">
              {posting ? "Sending…" : "Send"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
